import { useSupportCases } from "../../api/hooks";
import { Card, EmptyState, ErrorState, LoadingState, NotAvailable, RelativeTime, StatusPill } from "../../components/ui";

/** Read-only by requirement (requirements.md section 4): view tickets, never reply from here. */
export function SupportCasesTab({ environmentId }: { environmentId: string }) {
  const q = useSupportCases(environmentId);
  if (q.isPending) return <LoadingState />;
  if (q.isError) return <ErrorState error={q.error} onRetry={() => void q.refetch()} />;
  if (!q.data.available) {
    return (
      <NotAvailable
        what="AWS Support cases"
        why="Reading cases through the AWS Support API needs a Business or Enterprise support plan; this account's plan does not include it, so the poller is told SubscriptionRequiredException and stops. Billing and service-quota cases can still be opened and followed in the account's own AWS Support Center, and a quota increase requested from the Service Quotas tab links straight to its case there."
      />
    );
  }
  if (q.data.cases.length === 0) return <EmptyState title="No open support cases" />;
  return (
    <div className="grid gap-4">
      {q.data.cases.map((c) => (
        <Card key={c.case_id}>
          <article aria-labelledby={`case-${c.case_id}`}>
            <div className="flex flex-wrap items-start justify-between gap-2">
              <h3 id={`case-${c.case_id}`} className="font-medium">
                {c.subject} <span className="ml-2 font-mono text-xs text-neutral-500">{c.display_id ?? c.case_id}</span>
              </h3>
              <div className="flex items-center gap-2 text-sm">
                <StatusPill status={c.status} tone={/resolved|closed/i.test(c.status) ? "ok" : "warning"} />
                {c.severity && <StatusPill status={c.severity} tone="neutral" />}
              </div>
            </div>
            <p className="mt-1 text-xs text-neutral-500">
              {c.service ?? "AWS"} · updated <RelativeTime iso={c.last_updated} />
            </p>
            {c.last_communications.length > 0 && (
              <ol className="mt-3 space-y-2 border-l-2 border-neutral-200 pl-3 dark:border-neutral-700">
                {c.last_communications.map((m, i) => (
                  <li key={i} className="text-sm">
                    <p className="text-xs text-neutral-500">
                      {m.submitted_by ?? "unknown"} · <RelativeTime iso={m.time_created} />
                    </p>
                    <p className="mt-0.5 whitespace-pre-line">{m.body}</p>
                  </li>
                ))}
              </ol>
            )}
          </article>
        </Card>
      ))}
    </div>
  );
}
